import { t } from "@i18n";
import { observer } from "@legendapp/state/react";
import { CellProps } from "react-table";

import { useRemotePaging } from "_hooks/useMakeRemoteFilter";

import { default as _Table } from "_components/Table";
import { Buttons, ViewIconButton } from "_ui/Button";

import { useMyAttendanceSearch } from "_api/hooks/attendances";
import { Perms } from "_enums/Perms";
import { Attendance } from "_types/models/Attendance";
import { ConferenceDesk } from "_types/models/ConferenceDesk";
import { iso2view } from "_utils/date";
import { hasPerm } from "_utils/perms";
import { diff2time } from "_utils/time";
import { useRelativeGo } from "_utils/url";
import { optional } from "_utils/view";
import { $onFilterChange } from "../Filters";
import TableSuspense from "_components/suspense/TableSuspense";
import { Suspense } from "react";
import { AttendanceSituation, AttendanceSituationLabels } from "_enums/AttendanceSituation";

const actions = ({ value, row: { original: conferenceDesk } }: CellProps<ConferenceDesk>) => {
    const go = useRelativeGo();

    const view = (id: string) => {
        go(id, 'view');
    }

    return (
        <Buttons>
            <ViewIconButton onClick={() => view(value)} tooltip='Visualizar' />
        </Buttons>
    );
}

const DateCell = ({ value, row: { original: attendance } }: CellProps<Attendance>) => {
    return iso2view(attendance.startedAt);
};
const DurationCell = ({ value, row: { original: attendance } }: CellProps<Attendance>) => {
    return diff2time(attendance.finishedAt, attendance.startedAt);
};
const NpsCell = ({ value, row: { original: attendance } }: CellProps<Attendance>) => optional(attendance.npsNote);
const SituationCell = ({ value, row: { original: attendance } }: CellProps<Attendance>) => AttendanceSituationLabels[attendance.situation] || 'Não informado';

const Table = observer(() => {
    const columns = [
        {
            Header: 'Data',
            accessor: 'startedAt',
            Cell: DateCell,
        },
        {
            Header: 'Duração',
            accessor: 'finishedAt',
            Cell: DurationCell,
        },
        {
            Header: 'Solicitante',
            accessor: 'clientServed.name',
        },
        {
            Header: 'Situação',
            accessor: 'situation',
            Cell: SituationCell,
        },
        {
            Header: t('Actions'),
            accessor: 'id',
            Cell: actions,
        },
    ];

    const $paging = useRemotePaging();

    const { pageIndex, pageSize } = $paging;
    const filters = $onFilterChange
        .get()
        .toRequest();

    const { attendances: data, meta: paginated } = useMyAttendanceSearch({
        pageIndex: pageIndex.get(),
        pageSize: pageSize.get(),
        ...filters,
    });

    if (hasPerm([Perms.DeskConfNpsView])) {
        columns.splice(3, 0, {
            Header: 'NPS',
            accessor: 'npsNote',
            Cell: NpsCell,
        });
    }


    return <_Table
        {...{ columns, data, paginated }}
        onChangePage={pageIndex.set}
        onSelectPerPage={pageSize.set}
        isRemote
        isSortable
        pagination
    />
});

export const MyAttendancesTable = () => (
    <Suspense fallback={<TableSuspense columns={5} rows={6} />}>
        <Table />
    </Suspense>
)
